import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Star, CheckCircle, MapPin, Briefcase, Mail, Phone, FileText, Download, MessageSquare, Video, Loader, AlertCircle, BadgeCheck, Plane, Clock } from 'lucide-react';
import Button from '../../components/common/Button';
import authService from '../../api/authService';
import { toast } from 'react-toastify';
import professionalProfileApi from '../../api/professionalProfile';
import { useSearchParams } from 'react-router-dom';

const CandidateProfilePage = () => {
    const { candidateId: applicationId } = useParams();
    const navigate = useNavigate();
    const [application, setApplication] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pdfLoading, setPdfLoading] = useState(false);

    const [searchParams] = useSearchParams();
    const isProfileOnly = searchParams.get('type') === 'profile';

    const fetchDetails = async () => {
        try {
            setLoading(true);
            setError(null);

            if (isProfileOnly) {
                // Fetch directly from professional profile API (for search results)
                const { data } = await professionalProfileApi.getProfessionalDetails(applicationId);
                setApplication({ applicant: data, isProfileOnly: true });
            } else {
                // Fetch application details
                const { data } = await authService.getApplicationDetails(applicationId);
                setApplication(data);
            }
        } catch (err) {
            console.error("Error fetching details:", err);
            if (err.response?.status === 403) {
                setError({
                    message: err.response.data.message || "You don't have permission to view this profile.",
                    isForbidden: true,
                    verification_required: err.response.data.verification_required
                });
            } else {
                setError({ message: "Failed to load details. The content may no longer be available." });
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDetails();
    }, [applicationId, isProfileOnly]);

    // console.log("Application", application)

    const handleStatusUpdate = async (newStatus) => {
        try {
            const { status } = await authService.updateApplicationStatus(applicationId, { status: newStatus });
            if (status === 200) {
                toast.success("Status updated successfully")
                fetchApplicationDetails()
            }
        } catch (err) {
            console.error(err)
            if (err?.response?.data?.details) {
                const found = err.response.data.details.match(/reject/i)
                if (found) return setError("Cannot change status of an application that has been rejected.");
            }
            setError("Failed to update status.");
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[calc(100vh-160px)]">
                <Loader className="w-12 h-12 text-primary-500 animate-spin" />
            </div>
        );
    }

    if (error || !application) {
        return (
            <div className="max-w-5xl mx-auto py-8 px-4 text-center">
                <div className="bg-white p-8 rounded-2xl shadow-lg border max-w-md mx-auto">
                    <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                    <h2 className="text-xl font-bold text-secondary-800">
                        {error?.isForbidden ? "Access Restricted" : "An Error Occurred"}
                    </h2>
                    <p className="text-secondary-600 mt-2">{error?.message || 'Could not find the requested data.'}</p>

                    {error?.isForbidden && (
                        <div className="mt-6 space-y-3">
                            {error.verification_required ? (
                                <>
                                    <p className="text-sm text-secondary-500">
                                        To protect our community, we require all recruiters to verify their company profile before accessing professional details.
                                    </p>
                                    <div className="flex flex-col gap-3">
                                        <Button onClick={() => navigate('/company-profile')} className="w-full" variant="primary">
                                            Verify Company Profile
                                        </Button>
                                        <Button onClick={() => navigate('/account-settings')} className="w-full" variant="outline">
                                            Check Email Validation
                                        </Button>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <p className="text-sm text-secondary-500">
                                        Upgrade your subscription to gain full access to verified aviation professional profiles.
                                    </p>
                                    <Button onClick={() => navigate('/pricing')} className="w-full" variant="primary">
                                        View Subscription Plans
                                    </Button>
                                </>
                            )}
                        </div>
                    )}

                    <Button onClick={() => navigate(-1)} variant="outline" className="mt-4 w-full">
                        Go Back
                    </Button>
                </div>
            </div>
        );
    }

    const { applicant, job, cover_letter, documents: appDocuments, isProfileOnly: isProfileView } = application;

    // Normalize documents: if applicant.documents is an object (grouped by category), flatten it.
    // If it's an array, use it directly.
    const rawDocs = applicant?.documents || appDocuments;
    const documents = Array.isArray(rawDocs)
        ? rawDocs
        : rawDocs
            ? Object.values(rawDocs).flat()
            : [];

    // Helper to get verified status safely
    const isVerified = (typeof applicant?.verification_status === 'object'
        ? applicant?.verification_status?.is_verified
        : applicant?.verification_status) || false;

    // Safely extract aviation details
    const experienceData = applicant?.experiences?.[0] || applicant?.experience;
    const aviationData = applicant?.aviation_categories?.[0] || {};

    // Display values
    const totalHours = experienceData?.total_hours;
    const aircraftType = experienceData?.aircraft_type;
    const pilotType = experienceData?.pilot_type || aviationData?.pilot_type;

    const handleDownloadPdf = async () => {
        if (!applicant) return;
        const profileUserId = applicant.id || applicationId;
        try {
            setPdfLoading(true);
            const response = await professionalProfileApi.downloadProfilePdf(profileUserId);
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${applicant.full_name || 'candidate'}_profile.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('PDF download failed', err);
            toast.error('Failed to download PDF. Please try again.');
        } finally {
            setPdfLoading(false);
        }
    };

    return (
        <div className="bg-secondary-50 min-h-screen">
            <div className="max-w-full mx-auto py-8 px-6 lg:px-8">
                <div className="flex items-center justify-between mb-6">
                    <Button variant="link" onClick={() => navigate(-1)} className="flex items-center text-secondary-600">
                        <ArrowLeft className="w-5 h-5 mr-2" /> Back to Search
                    </Button>
                    {application && (
                        <Button
                            variant="outline"
                            onClick={handleDownloadPdf}
                            disabled={pdfLoading}
                            className="flex items-center gap-2 text-primary-700 border-primary-300 hover:bg-primary-50"
                        >
                            {pdfLoading ? (
                                <Loader className="w-4 h-4 animate-spin" />
                            ) : (
                                <Download className="w-4 h-4" />
                            )}
                            {pdfLoading ? 'Generating…' : 'Download PDF'}
                        </Button>
                    )}
                </div>
                <div className={`grid grid-cols-1 ${!isProfileView ? 'lg:grid-cols-4' : ''} gap-8`}>
                    <div className={`${!isProfileView ? 'lg:col-span-3' : 'w-full'} space-y-8`}>
                        <div className="bg-white p-8 rounded-xl shadow-sm border">
                            <div className="flex flex-col items-center space-y-6 sm:items-start sm:flex-row sm:space-x-6">
                                <img src={applicant?.profile_picture || `https://placehold.co/100x100/E2E8F0/475569?text=${applicant?.full_name?.charAt(0)}`} alt={applicant?.full_name} className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-md" />
                                <div className="flex-1 w-full">
                                    <div className="flex items-center justify-between w-full">
                                        <h1 className="text-3xl font-bold flex items-center gap-2">
                                            {applicant?.full_name}
                                            {isVerified && (
                                                <BadgeCheck className="w-6 h-6 text-blue-500" fill="currentColor" color="white" />
                                            )}
                                        </h1>
                                        {isVerified ? (
                                            <span className="px-3 py-1 bg-green-100 text-green-700 text-sm font-medium rounded-full flex items-center gap-1">
                                                <CheckCircle size={14} /> Verified
                                            </span>
                                        ) : (
                                            <span className="px-3 py-1 bg-gray-100 text-gray-600 text-sm font-medium rounded-full">
                                                Unverified
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-xl text-primary-600 font-semibold">{applicant?.specialization || 'Aviation Professional'}</p>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
                                        {totalHours && (
                                            <div className="flex items-center text-secondary-600">
                                                <Clock className="w-5 h-5 mr-2 text-primary-500" />
                                                <div>
                                                    <p className="text-xs text-secondary-400">Total Flight Hours</p>
                                                    <p className="font-medium text-sm">{totalHours}</p>
                                                </div>
                                            </div>
                                        )}
                                        {pilotType && (
                                            <div className="flex items-center text-secondary-600">
                                                <Star className="w-5 h-5 mr-2 text-primary-500" />
                                                <div>
                                                    <p className="text-xs text-secondary-400">Pilot Type</p>
                                                    <p className="font-medium text-sm">{pilotType}</p>
                                                </div>
                                            </div>
                                        )}
                                        {aircraftType && (
                                            <div className="flex items-center text-secondary-600 md:col-span-2">
                                                <Plane className="w-5 h-5 mr-2 text-primary-500" />
                                                <div>
                                                    <p className="text-xs text-secondary-400">Aircraft Type</p>
                                                    <p className="font-medium text-sm">{aircraftType}</p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                        {/* Detailed Sections Grid */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Employment History */}
                            {applicant?.employment_history?.length > 0 && (
                                <Section title="Employment History">
                                    <div className="space-y-8">
                                        {applicant.employment_history.map((job) => (
                                            <div key={job.id} className="relative pl-6 border-l-2 border-primary-200">
                                                <div className="absolute -left-[9px] top-0 w-4 h-4 rounded-full bg-white border-2 border-primary-500"></div>
                                                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-2">
                                                    <div>
                                                        <h4 className="font-bold text-lg text-secondary-900">{job.job_title}</h4>
                                                        <p className="text-primary-600 font-medium text-sm">{job.company_name}</p>
                                                    </div>
                                                    <span className="text-xs text-secondary-500 bg-secondary-50 px-2 py-1 rounded inline-block mt-2 sm:mt-0 self-start">
                                                        {job.start_date ? new Date(job.start_date).toLocaleDateString() : 'N/A'} - {job.is_current ? 'Present' : (job.end_date ? new Date(job.end_date).toLocaleDateString() : 'N/A')}
                                                    </span>
                                                </div>
                                                {job.responsibilities && <p className="text-secondary-600 text-sm leading-relaxed">{job.responsibilities}</p>}
                                            </div>
                                        ))}
                                    </div>
                                </Section>
                            )}

                            {/* Flight Experience */}
                            {applicant?.experiences?.length > 0 && (
                                <Section title="Flight Experience">
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full text-xs text-left">
                                            <thead className="bg-gray-50 text-secondary-500">
                                                <tr>
                                                    <th className="p-2 font-semibold rounded-tl-lg">Aircraft</th>
                                                    <th className="p-2 font-semibold">Pilot Type</th>
                                                    <th className="p-2 font-semibold">License</th>
                                                    <th className="p-2 font-semibold">Number</th>
                                                    <th className="p-2 font-semibold">Hours</th>
                                                    <th className="p-2 font-semibold">PIC</th>
                                                    <th className="p-2 font-semibold">SIC</th>
                                                    <th className="p-2 font-semibold">Expiry</th>
                                                    <th className="p-2 font-semibold rounded-tr-lg">Renewal</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-100">
                                                {applicant.experiences.map((exp) => (
                                                    <tr key={exp.id} className="hover:bg-gray-50 transition-colors">
                                                        <td className="p-2 font-medium text-secondary-900">{exp.aircraft_type || 'N/A'}</td>
                                                        <td className="p-2 capitalize">{exp.pilot_type?.replace('_', ' ') || '-'}</td>
                                                        <td className="p-2">{exp.license_type || '-'}</td>
                                                        <td className="p-2">{exp.license_number || '-'}</td>
                                                        <td className="p-2">{exp.total_hours || '-'}</td>
                                                        <td className="p-2">{exp.pilot_in_command_hours || '-'}</td>
                                                        <td className="p-2">{exp.first_officer_hours || '-'}</td>
                                                        <td className="p-2">{exp.expiry_date || '-'}</td>
                                                        <td className="p-2">{exp.renewal_date || '-'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </Section>
                            )}

                            {/* Licenses */}
                            {applicant?.licenses?.length > 0 && (
                                <Section title="Licenses">
                                    <div className="grid grid-cols-1 gap-4">
                                        {applicant.licenses.map((lic) => (
                                            <div key={lic.id} className="border border-gray-100 p-4 rounded-xl bg-gray-50 hover:border-primary-200 transition-colors">
                                                <div className="flex justify-between items-start mb-2">
                                                    <p className="font-bold text-sm text-secondary-900">{lic.license_type}</p>
                                                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${lic.license_status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                                        {lic.license_status_display}
                                                    </span>
                                                </div>
                                                <p className="text-xs text-secondary-500 mb-1">Number: {lic.license_number}</p>
                                                <p className="text-xs text-secondary-500">Expiry: {lic.expiry_date || 'N/A'}</p>
                                            </div>
                                        ))}
                                    </div>
                                </Section>
                            )}

                            {/* Education & Qualifications */}
                            {applicant?.qualifications?.length > 0 && (
                                <Section title="Education & Qualifications">
                                    <div className="grid grid-cols-1 gap-4">
                                        {applicant.qualifications.map((qual) => (
                                            <div key={qual.id} className="flex flex-col justify-between p-4 bg-gray-50 rounded-xl border border-gray-100">
                                                <div>
                                                    <h4 className="font-bold text-secondary-900">{qual.institution}</h4>
                                                    <p className="text-sm text-secondary-600 font-medium">{qual.course_of_study}</p>
                                                    <p className="text-xs text-secondary-500 capitalize mt-1">{qual.education_level_display}</p>
                                                </div>
                                                <div className="mt-3">
                                                    <span className="text-sm font-medium text-secondary-400 bg-white px-3 py-1 rounded-full border shadow-sm">
                                                        {qual.expected_graduation_year}
                                                    </span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </Section>
                            )}
                        </div>

                        {!isProfileView && (
                            <>
                                <Section title="Cover Letter">
                                    <p className="text-secondary-700 leading-relaxed whitespace-pre-line">{cover_letter}</p>
                                </Section>
                                <Section title="Documents">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {documents && documents.length > 0 ? documents.map(doc => (
                                            <div key={doc.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                                                <div className="flex items-center space-x-3">
                                                    <FileText className="w-5 h-5 text-primary-600" />
                                                    <div>
                                                        <p className="font-semibold text-sm truncate max-w-[150px]">{doc.name}</p>
                                                        <p className="text-xs text-gray-500">{doc.document_type.replace('_', ' ')}</p>
                                                    </div>
                                                </div>
                                                <a href={doc.download_url} target="_blank" rel="noopener noreferrer" className="p-2 rounded-full hover:bg-gray-200"><Download size={16} /></a>
                                            </div>
                                        )) : <p className="text-sm text-gray-500 col-span-2">No documents were submitted with this application.</p>}
                                    </div>
                                </Section>
                            </>
                        )}

                        {/* Show documents for Profile View if available and not hidden */}
                        {/* Show documents for Profile View if available and not hidden */}
                        {isProfileView && !applicant?.is_profile_hidden && documents && documents.length > 0 && (
                            <Section title="Documents">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {documents.map(doc => {
                                        const docName = doc.name || (doc.file ? doc.file.split('/').pop() : 'Document');
                                        const docType = doc.category || doc.document_type || 'Document';
                                        const docUrl = doc.file || doc.download_url || doc.file_url;

                                        return (
                                            <div key={doc.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                                                <div className="flex items-center space-x-3">
                                                    <FileText className="w-5 h-5 text-primary-600" />
                                                    <div>
                                                        <p className="font-semibold text-sm truncate max-w-[150px]">{docName}</p>
                                                        <p className="text-xs text-gray-500 capitalize">{docType.replace(/_/g, ' ')}</p>
                                                    </div>
                                                </div>
                                                {docUrl && (
                                                    <a href={docUrl} target="_blank" rel="noopener noreferrer" className="p-2 rounded-full hover:bg-gray-200">
                                                        <Download size={16} />
                                                    </a>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </Section>
                        )}
                    </div>

                    {!isProfileView && (
                        <div className="space-y-6">
                            <div className="bg-white p-6 rounded-xl shadow-sm border">
                                <h3 className="font-bold mb-4">Application Details</h3>
                                <p className="text-sm text-gray-600 mb-2">Applied for: <span className="font-semibold">{job?.title}</span></p>
                                <p className="text-sm text-gray-600">Company: <span className="font-semibold">{job?.recruiter_details?.company_name}</span></p>
                            </div>

                            <div className="bg-white p-6 rounded-xl shadow-sm border">
                                <h3 className="font-bold mb-4">Actions</h3>
                                <div className="space-y-3">
                                    <select onChange={(e) => handleStatusUpdate(e.target.value)} value={application.status} className="w-full p-2.5 border rounded-lg bg-gray-50">
                                        <option value="submitted">Submitted</option>
                                        <option value="under_review">Under Review</option>
                                        <option value="shortlisted">Shortlisted</option>
                                        <option value="interview">Interview</option>
                                        <option value="offer_extended">Offer</option>
                                        <option value="hired">Hired</option>
                                        <option value="rejected">Rejected</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const Section = ({ title, children }) => (
    <div className="bg-white p-8 rounded-xl shadow-sm border">
        <h2 className="text-xl font-bold mb-4">{title}</h2>
        {children}
    </div>
);

export default CandidateProfilePage;

